/**
 * Bottom sheet de feedback pós-treino do aluno.
 *
 * Coleta 4 sinais: intensidade (1-5), cansaço (1-5), dor (sim/não + local) e
 * um RECADO livre opcional pra personal (observations) — texto multiline.
 * Tudo zera toda vez que o modal reabre (useEffect em `visible`).
 *
 * Props:
 *   - visible: controla exibição
 *   - onSave(data): chamado ao enviar; recebe FeedbackData completo
 *   - onSkip: pular sem salvar (também usado no fechar)
 *
 * Decisão: observations vazio é normalizado pra `null` no save (não grava string vazia).
 */
import { View, Text, Modal, TouchableOpacity, TextInput, StyleSheet, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { useState, useEffect } from 'react';

export interface FeedbackData {
  intensity: number;
  fatigue: number;
  had_pain: boolean;
  pain_location: string | null;
  /** Recado livre do aluno pra personal. null quando não preenchido. */
  observations: string | null;
}

interface Props {
  visible: boolean;
  onSave: (data: FeedbackData) => void;
  onSkip: () => void;
}

const INTENSITY_EMOJIS = ['😴', '🙂', '💪', '🔥', '🤯'];
const FATIGUE_EMOJIS = ['😊', '🙂', '😓', '😩', '💀'];
const PAIN_LOCATIONS = ['Costas', 'Joelho', 'Ombro', 'Quadril', 'Pescoço', 'Outro'];

export function WorkoutFeedbackModal({ visible, onSave, onSkip }: Props) {
  const [intensity, setIntensity] = useState(3);
  const [fatigue, setFatigue] = useState(3);
  const [hadPain, setHadPain] = useState(false);
  const [painLocation, setPainLocation] = useState<string | null>(null);
  const [observations, setObservations] = useState('');

  useEffect(() => {
    if (visible) {
      setIntensity(3);
      setFatigue(3);
      setHadPain(false);
      setPainLocation(null);
      setObservations('');
    }
  }, [visible]);

  function handleSave() {
    onSave({
      intensity,
      fatigue,
      had_pain: hadPain,
      pain_location: hadPain ? painLocation : null,
      observations: observations.trim() || null,
    });
  }

  function handleNoPain() {
    setHadPain(false);
    setPainLocation(null);
  }

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onSkip}>
      <KeyboardAvoidingView
        style={styles.bg}
        behavior="padding"
      >
        <View style={styles.card}>
          <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          <Text style={styles.title}>Como foi o treino?</Text>
          <Text style={styles.sub}>Seu feedback ajuda a personal a ajustar sua rotina.</Text>

          <Text style={styles.label}>Intensidade</Text>
          <View style={styles.emojiRow}>
            {INTENSITY_EMOJIS.map((emoji, i) => (
              <TouchableOpacity
                key={emoji}
                style={[styles.emojiBtn, intensity === i + 1 && styles.emojiBtnActive]}
                onPress={() => setIntensity(i + 1)}
              >
                <Text style={styles.emoji}>{emoji}</Text>
                <Text style={[styles.emojiNum, intensity === i + 1 && styles.emojiNumActive]}>{i + 1}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.label}>Cansaço</Text>
          <View style={styles.emojiRow}>
            {FATIGUE_EMOJIS.map((emoji, i) => (
              <TouchableOpacity
                key={emoji}
                style={[styles.emojiBtn, fatigue === i + 1 && styles.emojiBtnActive]}
                onPress={() => setFatigue(i + 1)}
              >
                <Text style={styles.emoji}>{emoji}</Text>
                <Text style={[styles.emojiNum, fatigue === i + 1 && styles.emojiNumActive]}>{i + 1}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={styles.label}>Sentiu alguma dor?</Text>
          <View style={styles.painRow}>
            <TouchableOpacity
              style={[styles.painBtn, !hadPain && styles.painBtnNo]}
              onPress={handleNoPain}
            >
              <Text style={[styles.painBtnText, !hadPain && styles.painBtnTextNo]}>Não</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.painBtn, hadPain && styles.painBtnYes]}
              onPress={() => setHadPain(true)}
            >
              <Text style={[styles.painBtnText, hadPain && styles.painBtnTextYes]}>Sim</Text>
            </TouchableOpacity>
          </View>

          {hadPain && (
            <>
              <Text style={styles.label}>Onde?</Text>
              <View style={styles.locationRow}>
                {PAIN_LOCATIONS.map(loc => (
                  <TouchableOpacity
                    key={loc}
                    style={[styles.locationChip, painLocation === loc && styles.locationChipActive]}
                    onPress={() => setPainLocation(loc)}
                  >
                    <Text style={[styles.locationText, painLocation === loc && styles.locationTextActive]}>
                      {loc}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}

          <Text style={styles.label}>Observações (opcional)</Text>
          <TextInput
            style={styles.obsInput}
            value={observations}
            onChangeText={setObservations}
            placeholder="Quer deixar um recado pra sua personal? Conte como foi o treino..."
            placeholderTextColor="#bbb"
            multiline
            numberOfLines={3}
            maxLength={500}
            textAlignVertical="top"
          />

          <TouchableOpacity style={styles.saveBtn} onPress={handleSave}>
            <Text style={styles.saveBtnText}>Enviar feedback</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.skipBtn} onPress={onSkip}>
            <Text style={styles.skipBtnText}>Pular</Text>
          </TouchableOpacity>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  bg: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  card: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, paddingBottom: 40, maxHeight: '90%' },
  title: { fontSize: 20, fontWeight: '700', color: '#1A1A1A', marginBottom: 4 },
  sub: { fontSize: 13, color: '#888888', marginBottom: 8 },
  label: { fontSize: 13, fontWeight: '600', color: '#555', marginBottom: 8, marginTop: 14 },
  emojiRow: { flexDirection: 'row', gap: 6 },
  emojiBtn: { flex: 1, alignItems: 'center', paddingVertical: 8, borderRadius: 10, borderWidth: 1.5, borderColor: '#eee', backgroundColor: '#fafafa' },
  emojiBtnActive: { borderColor: '#C8102E', backgroundColor: '#FFF0F2' },
  emoji: { fontSize: 20 },
  emojiNum: { fontSize: 11, color: '#aaa', marginTop: 2 },
  emojiNumActive: { color: '#C8102E', fontWeight: '700' },
  painRow: { flexDirection: 'row', gap: 10 },
  painBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, borderWidth: 1.5, borderColor: '#eee', alignItems: 'center', backgroundColor: '#fafafa' },
  painBtnNo: { borderColor: '#2E7D32', backgroundColor: '#F0FFF4' },
  painBtnYes: { borderColor: '#C8102E', backgroundColor: '#FFF0F2' },
  painBtnText: { fontSize: 14, fontWeight: '600', color: '#555' },
  painBtnTextNo: { color: '#2E7D32' },
  painBtnTextYes: { color: '#C8102E' },
  locationRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  locationChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5, borderColor: '#eee', backgroundColor: '#fafafa' },
  locationChipActive: { borderColor: '#C8102E', backgroundColor: '#FFF0F2' },
  locationText: { fontSize: 13, color: '#555' },
  locationTextActive: { color: '#C8102E', fontWeight: '600' },
  obsInput: { borderWidth: 1.5, borderColor: '#eee', borderRadius: 10, backgroundColor: '#fafafa', padding: 12, fontSize: 14, color: '#1A1A1A', minHeight: 72, marginTop: 4 },
  saveBtn: { marginTop: 22, backgroundColor: '#C8102E', borderRadius: 12, padding: 16, alignItems: 'center' },
  saveBtnText: { color: '#fff', fontWeight: '700', fontSize: 16 },
  skipBtn: { marginTop: 8, padding: 12, alignItems: 'center' },
  skipBtnText: { color: '#888', fontSize: 14 },
});
